// Shared chat-message model + pure message-list logic, used by both the live
// workflow chat (WorkflowChat / useWorkflowChat) and the public conversation
// replay page (/share/chat/<shortcode>). Keep this free of React and Convex
// imports so either side can use it.

export interface ToolCall {
  id: string
  name: string
  input: Record<string, unknown> | null
  status: 'running' | 'completed' | 'failed'
  result?: {
    success: boolean
    data?: unknown
    error?: string
  }
}

export interface ImageAttachment {
  type: 'image'
  url: string
  storageId?: string
  name?: string
}

export interface VideoAttachment {
  type: 'video'
  url: string
  storageId?: string
  name?: string
  mimeType: string
  duration?: number
}

export interface AudioAttachment {
  type: 'audio'
  url: string
  storageId?: string
  name?: string
  duration: number
  mimeType: string
}

export interface DocumentAttachment {
  type: 'document'
  url: string
  storageId?: string
  name?: string
  mimeType: string
  extractedText?: string
  pageCount?: number
}

export type ChatAttachment = ImageAttachment | VideoAttachment | AudioAttachment | DocumentAttachment

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp?: number
  attachments?: ChatAttachment[]
  toolCalls?: ToolCall[]
  contentAfterTools?: string
  suggestedActions?: string[]
  isStreaming?: boolean
}

// Raw workflowMessages doc shape (the subset the UI consumes). Structurally
// matches both Doc<'workflowMessages'> and the sanitized payload returned by
// workflows.getSharedConversation.
export interface WorkflowMessageDoc {
  _id: string
  _creationTime: number
  role: string
  content: string
  attachments?: unknown
  toolCalls?: Array<{ id: string; name: string; input: unknown }>
  toolResults?: Array<{ toolCallId: string; result: unknown }>
  suggestedActions?: string[]
  isStreaming?: boolean
}

export function convertWorkflowMessages(docs: WorkflowMessageDoc[]): ChatMessage[] {
  return docs.map((m) => {
    const completedToolIds = new Set(m.toolResults?.map((tr) => tr.toolCallId) || [])

    return {
      id: m._id as string,
      role: m.role as 'user' | 'assistant' | 'system',
      content: m.content,
      timestamp: m._creationTime,
      attachments: m.attachments as ChatAttachment[] | undefined,
      toolCalls: m.toolCalls?.map((tc) => {
        const hasResult = completedToolIds.has(tc.id)
        const result = m.toolResults?.find((tr) => tr.toolCallId === tc.id)
        const resultPayload = result?.result as { success?: boolean; data?: unknown; error?: string } | undefined
        const isFailure = resultPayload?.success === false || !!resultPayload?.error

        return {
          id: tc.id,
          name: tc.name,
          input: tc.input as Record<string, unknown>,
          status: hasResult
            ? (isFailure ? 'failed' : 'completed')
            : 'running',
          result: resultPayload as { success: boolean; data?: unknown; error?: string } | undefined,
        } as ToolCall
      }),
      suggestedActions: m.suggestedActions as string[] | undefined,
      isStreaming: m.isStreaming ?? false,
    }
  })
}

// Consecutive tool-only assistant messages are displayed as one aggregated
// tool section at the position of the first message in the run.
export function computeAggregatedToolCalls(messages: ChatMessage[]): {
  groups: Map<number, ToolCall[]>
  seenToolIds: Set<string>
} {
  const result = new Map<number, ToolCall[]>() // index -> aggregated tools for display at that index
  const seenToolIds = new Set<string>()

  let currentGroup: ToolCall[] = []
  let groupStartIndex = -1

  const flush = () => {
    if (currentGroup.length > 0 && groupStartIndex >= 0) {
      result.set(groupStartIndex, currentGroup)
      currentGroup.forEach((t) => seenToolIds.add(t.id))
    }
    currentGroup = []
    groupStartIndex = -1
  }

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]

    if (msg.role === 'user' || msg.role === 'system') {
      flush()
    } else if (msg.role === 'assistant') {
      const hasContent = msg.content?.trim() || msg.contentAfterTools?.trim()
      const hasTools = msg.toolCalls?.length

      if (hasTools && !hasContent) {
        // Tool-only message: aggregate with other tool-only messages
        if (groupStartIndex === -1) groupStartIndex = i
        currentGroup.push(...msg.toolCalls!)
      } else if (hasContent) {
        // Message with content breaks the group (tools rendered inline by MessageBubble)
        flush()
      }
    }
  }

  flush()

  return { groups: result, seenToolIds }
}

// Track terminal status for each "generation begun" system message by its ID.
// Each begun message is matched to its terminal message (the next
// ready/cancelled/failed for that scene). Same for export started/finished.
export function computeMessageTerminalStatus(
  messages: ChatMessage[]
): Map<string, 'ready' | 'cancelled' | 'failed'> {
  // First, collect all "begun" messages with their scene names
  const begunMessages: Array<{ id: string; sceneName: string; index: number }> = []
  messages.forEach((msg, idx) => {
    if (msg.role === 'system') {
      const begunMatch = msg.content.match(/Clip (?:\d+ )?"([^"]+)" video generation begun/)
      if (begunMatch) {
        begunMessages.push({ id: msg.id, sceneName: begunMatch[1], index: idx })
      }
    }
  })

  // For each terminal message, match it to the most recent unmatched "begun" for that scene
  const result = new Map<string, 'ready' | 'cancelled' | 'failed'>() // messageId -> status
  const matchedBegunIds = new Set<string>()

  messages.forEach((msg, idx) => {
    if (msg.role === 'system') {
      let sceneName: string | null = null
      let status: 'ready' | 'cancelled' | 'failed' | null = null

      const readyMatch = msg.content.match(/Clip (?:\d+ )?"([^"]+)" video ready/)
      if (readyMatch) { sceneName = readyMatch[1]; status = 'ready' }

      const cancelledMatch = msg.content.match(/Clip (?:\d+ )?"([^"]+)".*cancelled/i)
      if (cancelledMatch) { sceneName = cancelledMatch[1]; status = 'cancelled' }

      const failedMatch = msg.content.match(/\[GENERATION_FAILED\].*Clip (?:\d+ )?"([^"]+)"/)
      if (failedMatch) { sceneName = failedMatch[1]; status = 'failed' }

      if (sceneName && status) {
        // Find the most recent unmatched "begun" for this scene (before this message)
        for (let i = begunMessages.length - 1; i >= 0; i--) {
          const begun = begunMessages[i]
          if (begun.sceneName === sceneName && begun.index < idx && !matchedBegunIds.has(begun.id)) {
            matchedBegunIds.add(begun.id)
            result.set(begun.id, status)
            break
          }
        }
      }
    }
  })

  // Track export started → complete/failed
  // Collect all pending export-started IDs so re-exports don't orphan earlier ones
  let pendingExportIds: string[] = []
  messages.forEach((msg) => {
    if (msg.role === 'system') {
      if (msg.content.startsWith('[EXPORT_STARTED]')) {
        pendingExportIds.push(msg.id)
      } else if (msg.content.startsWith('[EXPORT_COMPLETE]') && pendingExportIds.length > 0) {
        for (const id of pendingExportIds) result.set(id, 'ready')
        pendingExportIds = []
      } else if (msg.content.startsWith('[EXPORT_FAILED]') && pendingExportIds.length > 0) {
        for (const id of pendingExportIds) result.set(id, 'failed')
        pendingExportIds = []
      }
    }
  })

  return result
}

// The most recent completed treatment_board tool call — older boards render as
// "superseded" in the UI.
export function computeLatestTreatmentToolId(messages: ChatMessage[]): string | null {
  let latest: string | null = null
  for (const msg of messages) {
    for (const tc of msg.toolCalls ?? []) {
      if (
        tc.status === 'completed' &&
        tc.result?.success &&
        (tc.result?.data as { type?: string })?.type === 'treatment_board'
      ) {
        latest = tc.id
      }
    }
  }
  return latest
}

// Split an assistant message into user-visible text and an internal error tag
// (agent failures are stored inline with [AGENT_ERROR]/Error: markers).
export function parseAgentErrorContent(content: string): { visibleContent: string; error: string | null } {
  const markerMatch = content.match(/^\[AGENT_ERROR\]\s*([\s\S]+)$/i)
  if (markerMatch?.[1]) {
    return { visibleContent: '', error: markerMatch[1].trim() || null }
  }

  const fullLegacyMatch = content.match(/^Error:\s*([\s\S]+)$/i)
  if (fullLegacyMatch?.[1]) {
    return { visibleContent: '', error: fullLegacyMatch[1].trim() || null }
  }

  const suffixLegacyMatch = content.match(/^([\s\S]*?)\n\nError:\s*([\s\S]+)$/i)
  if (suffixLegacyMatch?.[2]) {
    return {
      visibleContent: suffixLegacyMatch[1].trim(),
      error: suffixLegacyMatch[2].trim() || null,
    }
  }

  return { visibleContent: content, error: null }
}

// Find the user's recorded answer (next zero-width-space-prefixed user message)
// for a question/treatment widget at `index` — used for reload persistence.
export function findExistingAnswer(messages: ChatMessage[], index: number): string | undefined {
  const message = messages[index]
  if (
    message.role !== 'assistant' ||
    !message.toolCalls?.some(
      (tc) =>
        tc.status === 'completed' &&
        tc.result?.success &&
        ['question_sequence', 'question_options', 'treatment_board'].includes(
          (tc.result?.data as { type?: string })?.type || ''
        )
    )
  ) {
    return undefined
  }
  for (let j = index + 1; j < messages.length; j++) {
    if (messages[j].role === 'user' && messages[j].content?.startsWith('\u200B')) {
      return messages[j].content
    }
  }
  return undefined
}
